import {Banner} from '@wandb/components';
import React, {useEffect, useState} from 'react';

import {backendHost} from '../../config';

type LimitResponse = {
  limit_statuses?: {
    distinct_metrics_per_run?: {
      available: boolean;
      usage?: number;
      limit: number;
      warning?: boolean;
      blocked?: boolean;
    };
  };
};

type Props = {entityName: string; projectName: string; runName: string};

const useRunMetricLimit = ({entityName, projectName, runName}: Props) => {
  const [response, setResponse] = useState<LimitResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    const path = [entityName, projectName, runName]
      .map(encodeURIComponent)
      .join('/');
    setResponse(null);
    const fetchStatus = async () => {
      try {
        // backendHost(path) drops the path for alternate hosts; append it to the base.
        // oxlint-disable-next-line wandb/no-unprefixed-urls
        const result = await fetch(
          `${backendHost()}/runs/${path}/metric_limit_status`,
          {
            credentials: 'include',
            signal: controller.signal,
          }
        );
        if (result.ok) {
          const data: LimitResponse = await result.json();
          if (!controller.signal.aborted) {
            setResponse(data);
          }
        } else if (!controller.signal.aborted) {
          setResponse(null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setResponse(null);
        }
      }
    };
    void fetchStatus();
    return () => {
      controller.abort();
    };
  }, [entityName, projectName, runName]);

  return response;
};

export const RunMetricLimitBanner = (props: Props) => {
  const response = useRunMetricLimit(props);
  const status = response?.limit_statuses?.distinct_metrics_per_run;
  if (
    !status?.available ||
    typeof status.usage !== 'number' ||
    (!status.warning && !status.blocked)
  ) {
    return null;
  }
  return (
    <div className="px-16 pt-8" role="status">
      <Banner
        variant={status.blocked ? 'error' : 'warning'}
        isDismissible={false}>
        <Banner.Body>
          <strong>
            {status.blocked
              ? 'Run metric limit exceeded. '
              : `This run has used ${Math.floor((status.usage / status.limit) * 100)}% of its distinct metric limit. `}
          </strong>
          {status.blocked
            ? `Further ingestion for this run is blocked. Previously accepted data remains available. Start a new run with fewer metric names or contact support. The limit is ${status.limit.toLocaleString()} distinct metric names.`
            : 'Consider logging fewer distinct metric names in future runs to avoid reaching the limit. Exceeding the limit blocks further uploads to this run.'}
        </Banner.Body>
      </Banner>
    </div>
  );
};
